import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { track } from "@/lib/analytics";
import PriceBlock from "./PriceBlock";
import OutletChip from "./OutletChip";

export default function PackageDetail({ pkg }) {
  const { isAuthenticated } = useAuth();
  const getStartedTo = isAuthenticated ? "/dashboard/new-order" : "/register";
  const isPopular = pkg.badge === "Most Popular";

  const viewedRef = useRef(false);
  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    track("view_item", {
      items: [{ item_id: pkg.slug || pkg.id || pkg.name || "", item_name: pkg.name || "", item_category: pkg.category_tag || "", price: Number(pkg.sale_price) || 0, quantity: 1 }],
    });
  }, []);

  return (
    <section id={pkg.slug} className="scroll-mt-24">
      <div
        className={`rounded-[20px] border p-6 sm:p-8 lg:p-10 ${
          isPopular
            ? "glass-featured bg-[rgba(255,255,255,0.58)] backdrop-blur-xl"
            : "bg-[rgba(255,255,255,0.58)] backdrop-blur-xl border-[rgba(255,255,255,0.85)]"
        }`}
        style={{ boxShadow: "0 12px 32px rgba(90,60,160,0.08)" }}
      >
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          <div>
            <span className="block font-mono text-xs uppercase tracking-widest text-[#6c6c74] mb-3">
              {pkg.category_tag}
            </span>
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h2 className="font-heading text-2xl sm:text-3xl font-[650] text-[#1c1c20]">
                {pkg.name}
              </h2>
              {pkg.badge && (
                <span
                  className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-md ${
                    isPopular
                      ? "bg-brand-gradient text-white"
                      : "border border-[rgba(28,28,32,0.08)] text-[#6c6c74]"
                  }`}
                >
                  {pkg.badge}
                </span>
              )}
            </div>
            <p className="text-[#7c3aed] text-[15px] font-medium mb-4">{pkg.tagline}</p>
            <p className="text-[#55555c] text-[15px] leading-[1.7] mb-6">{pkg.description}</p>

            <span className="block font-mono text-xs uppercase tracking-widest text-[#6c6c74] mb-3">
              What's inside
            </span>
            {(() => {
              const comps = pkg.components || [];
              if (comps.length > 0) {
                const groups = [
                  comps.filter((c) => c.category === "guaranteed_placements"),
                  comps.filter((c) => c.category === "newswires"),
                  comps.filter(
                    (c) =>
                      c.category !== "guaranteed_placements" &&
                      c.category !== "newswires"
                  ),
                ];
                return (
                  <div className="flex flex-col gap-2.5">
                    {groups
                      .filter((g) => g.length > 0)
                      .map((group, gi) => (
                        <div key={gi} className="flex flex-wrap gap-2">
                          {group.map((c) => {
                            if (
                              c.category === "newswires" &&
                              c.media_outlets_count != null
                            ) {
                              return (
                                <span key={c.id} className="inline-flex flex-col">
                                  <OutletChip component={c} />
                                  <span className="text-[12px] text-[#8e8e96] mt-0.5">
                                    {`Syndicated to ${c.media_outlets_count}+ outlets`}
                                  </span>
                                </span>
                              );
                            }
                            return <OutletChip key={c.id} component={c} />;
                          })}
                        </div>
                      ))}
                  </div>
                );
              }
              if (comps.length === 0 && pkg.name && pkg.name.startsWith("MSN")) {
                const match = pkg.name.match(/\+(\d+)/);
                const countLabel = match ? `+${match[1]} syndication sites` : "";
                const sampleUrls = {
                  "MSN +20": "https://base44.app/api/apps/6a460432963500ce22897197/files/mp/public/6a460432963500ce22897197/f8a96f992_MSNAuthority20-SampleList.pdf",
                  "MSN +30": "https://base44.app/api/apps/6a460432963500ce22897197/files/mp/public/6a460432963500ce22897197/a355db939_MSNAuthority30-SampleList.pdf",
                  "MSN +40": "https://base44.app/api/apps/6a460432963500ce22897197/files/mp/public/6a460432963500ce22897197/107bdad1e_MSNAuthority40-SampleList.pdf",
                };
                const sampleUrl = sampleUrls[pkg.name] || "";
                return (
                  <div>
                    <div className="flex flex-wrap gap-2">
                      <OutletChip component={{ id: pkg.id, name: "MSN", logo_url: pkg.logo_url || "", brand_logo_url: "" }} />
                      {countLabel && (
                        <span className="inline-flex items-center bg-[rgba(139,92,246,0.09)] border border-[rgba(139,92,246,0.2)] text-[#7c3aed] text-[12px] font-medium rounded-lg px-2 py-1 whitespace-nowrap">
                          {countLabel}
                        </span>
                      )}
                    </div>
                    {sampleUrl && (
                      <a
                        href={sampleUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#7c3aed] hover:text-[#1c1c20] text-sm font-semibold transition-colors mt-2"
                      >
                        View the full site list
                      </a>
                    )}
                  </div>
                );
              }
              return (
                <ul className="space-y-2.5">
                  {(pkg.included_items || []).map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-[#55555c] text-[15px] leading-[1.6]">
                      <Check className="w-4 h-4 text-[#7c3aed] shrink-0 mt-1" /> {item}
                    </li>
                  ))}
                </ul>
              );
            })()}
          </div>

          <div className="lg:border-l lg:border-[rgba(28,28,32,0.06)] lg:pl-12">
            <span className="block font-mono text-xs uppercase tracking-widest text-[#6c6c74] mb-2">
              Who it's for
            </span>
            <p className="text-[#55555c] text-[15px] leading-[1.7] mb-6">{pkg.who_for}</p>

            <span className="block font-mono text-xs uppercase tracking-widest text-[#6c6c74] mb-2">
              Why our team built it
            </span>
            <p className="text-[#55555c] text-[15px] leading-[1.7] mb-8">{pkg.why_built}</p>

            <div className="pt-6 border-t border-[rgba(28,28,32,0.06)]">
              <PriceBlock original={pkg.original_price} sale={pkg.sale_price} variant="detail" />
              <Link
                to={getStartedTo}
                data-track-label="get_started"
                data-track-section="pricing"
                className="inline-flex items-center bg-brand-gradient text-white font-medium px-6 py-3 rounded-[14px] text-[15px] transition-opacity hover:opacity-90 mt-5"
                style={{ boxShadow: "0 8px 20px rgba(124,58,237,0.35)" }}
              >
                Get Started <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}